import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, IconButton, Alert, Button } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AgentSidebar from './components/AgentSidebar';
import NodeCanvas from './canvas/NodeCanvas';
import NodePalette from './canvas/NodePalette';
import NodeProperties from './canvas/NodeProperties';
import CanvasVersionBar from './canvas/CanvasVersionBar';
import PromptVisualizerPanel from './canvas/PromptVisualizerPanel';
import DiffOverlay from './canvas/diff/DiffOverlay';

export default function CanvasPage() {
  const { agentName } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [selectedElement, setSelectedElement] = useState(null);
  const setNodesRef = useRef(null);
  const setEdgesRef = useRef(null);
  const edgesRef = useRef(null);
  const addToolNodeRef = useRef(null);
  const addSubAgentNodeRef = useRef(null);
  const undoRef = useRef(null);
  const discardDraftRef = useRef(null);
  const reloadRef = useRef(null);
  const versionsRef = useRef([]);
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffVersionId, setDiffVersionId] = useState(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canvasNodes, setCanvasNodes] = useState([]);
  const [canvasEdges, setCanvasEdges] = useState([]);
  const [hasDraft, setHasDraft] = useState(false);
  const [staleDraft, setStaleDraft] = useState(null);
  const [propertiesWidth, setPropertiesWidth] = useState(260);
  const [propertiesCollapsed, setPropertiesCollapsed] = useState(false);
  const [agentCollapsed, setAgentCollapsed] = useState(false);
  const [paletteCollapsed, setPaletteCollapsed] = useState(false);
  const dragState = useRef(null);

  useEffect(() => {
    if (selectedElement) {
      setPropertiesCollapsed(false);
      setAgentCollapsed(true);
    }
  }, [selectedElement]);

  // Navigating to a different agent must clear the previous selection — the
  // node IDs belong to the previous canvas and the panel would otherwise show
  // stale data from it.
  useEffect(() => {
    setSelectedElement(null);
    setStaleDraft(null);
  }, [agentName]);

  const handleDividerMouseDown = useCallback((e) => {
    e.preventDefault();
    dragState.current = { startX: e.clientX, startWidth: propertiesWidth };
    const onMove = (e) => {
      if (!dragState.current) return;
      const delta = e.clientX - dragState.current.startX;
      setPropertiesWidth(Math.max(180, Math.min(1200, dragState.current.startWidth + delta)));
    };
    const onUp = () => {
      dragState.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [propertiesWidth]);

  const handleUpdateNode = useCallback((nodeId, dataUpdate) => {
    if (!setNodesRef.current) return;
    setNodesRef.current((prev) =>
      prev.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...dataUpdate } } : n)),
    );
    // Keep selectedElement in sync — any code that updates canvas nodes must also
    // update selectedElement here, or derive selectedElement from nodes array instead.
    setSelectedElement((prev) =>
      prev?.id === nodeId ? { ...prev, data: { ...prev.data, ...dataUpdate } } : prev,
    );
  }, []);

  const handleDeleteNode = useCallback((nodeId) => {
    if (!setNodesRef.current || !setEdgesRef.current) return;
    setNodesRef.current((prev) => prev.filter((n) => n.id !== nodeId));
    setEdgesRef.current((prev) => prev.filter((e) => e.source !== nodeId && e.target !== nodeId));
    setSelectedElement((prev) => (prev?.id === nodeId ? null : prev));
  }, []);

  const handleDuplicateNode = useCallback((node) => {
    if (!setNodesRef.current) return;
    const copy = {
      ...node,
      id: crypto.randomUUID(),
      position: { x: node.position.x + 40, y: node.position.y + 40 },
      data: { ...node.data },
      selected: false,
    };
    setNodesRef.current((prev) => [...prev, copy]);
  }, []);

  const handleUpdateEdge = useCallback((edgeId, dataUpdate) => {
    if (!setEdgesRef.current) return;
    setEdgesRef.current((prev) =>
      prev.map((e) => (e.id === edgeId ? { ...e, data: { ...e.data, ...dataUpdate } } : e)),
    );
    setSelectedElement((prev) =>
      prev?.id === edgeId ? { ...prev, data: { ...prev.data, ...dataUpdate } } : prev,
    );
  }, []);

  const handleOpenCanvas = useCallback((subAgentName) => {
    navigate(`/admin/agent-builder/${subAgentName}`);
  }, [navigate]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <CanvasVersionBar
        key={agentName}
        agentName={agentName}
        canvasNodes={canvasNodes}
        canvasEdges={canvasEdges}
        hasDraft={hasDraft}
        onDraftSaved={() => setHasDraft(false)}
        canUndo={canUndo}
        undoRef={undoRef}
        discardDraftRef={discardDraftRef}
        reloadRef={reloadRef}
        versionsRef={versionsRef}
        onDiffRequested={(versionId) => {
          setDiffVersionId(versionId);
          setDiffOpen(true);
        }}
      />
      <DiffOverlay
        open={diffOpen}
        onClose={() => setDiffOpen(false)}
        agentName={agentName}
        liveNodes={canvasNodes}
        liveEdges={canvasEdges}
        versions={versionsRef.current}
        initialVersionId={diffVersionId}
      />
      {staleDraft && (
        <Alert
          severity="warning"
          sx={{ borderRadius: 0 }}
          action={
            <>
              <Button color="inherit" size="small" onClick={() => { staleDraft.discard(); setStaleDraft(null); }}>
                Discard draft &amp; load server version
              </Button>
              <Button color="inherit" size="small" onClick={() => { staleDraft.keep(); setStaleDraft(null); }}>
                Keep my draft
              </Button>
            </>
          }
        >
          Your unsaved draft is out of sync with the server (someone else saved, or the canvas was re-seeded).
        </Alert>
      )}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <AgentSidebar collapsed={agentCollapsed} onToggle={() => setAgentCollapsed((v) => !v)} />
        {propertiesCollapsed ? (
          <Box sx={{ width: 28, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'flex-start', pt: 0.5, justifyContent: 'center' }}>
            <IconButton size="small" onClick={() => setPropertiesCollapsed(false)} title="Show properties">
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <>
            <NodeProperties
              selectedElement={selectedElement}
              onUpdateNode={handleUpdateNode}
              onUpdateEdge={handleUpdateEdge}
              onDeleteNode={handleDeleteNode}
              onDuplicateNode={handleDuplicateNode}
              agentName={agentName}
              addToolNodeRef={addToolNodeRef}
              addSubAgentNodeRef={addSubAgentNodeRef}
              onOpenCanvas={handleOpenCanvas}
              width={propertiesWidth}
              onCollapse={() => setPropertiesCollapsed(true)}
              canvasNodes={canvasNodes}
              canvasEdges={canvasEdges}
            />
            <Box
              onMouseDown={handleDividerMouseDown}
              sx={{ width: 4, flexShrink: 0, cursor: 'col-resize', bgcolor: 'divider', '&:hover': { bgcolor: 'primary.main' }, transition: 'background-color 0.15s' }}
            />
          </>
        )}
        <NodeCanvas
          key={pathname}
          agentName={agentName}
          onSelectNode={setSelectedElement}
          onOpenCanvas={handleOpenCanvas}
          setNodesRef={setNodesRef}
          setEdgesRef={setEdgesRef}
          edgesRef={edgesRef}
          addToolNodeRef={addToolNodeRef}
          addSubAgentNodeRef={addSubAgentNodeRef}
          onNodesSnapshot={setCanvasNodes}
          onEdgesSnapshot={setCanvasEdges}
          onDraftChange={setHasDraft}
          onStaleDraft={setStaleDraft}
          undoRef={undoRef}
          discardDraftRef={discardDraftRef}
          reloadRef={reloadRef}
          onUndoStateChange={setCanUndo}
        />
        <PromptVisualizerPanel nodes={canvasNodes} edges={canvasEdges} onOpen={() => setPaletteCollapsed(true)} />
        <NodePalette collapsed={paletteCollapsed} onToggle={() => setPaletteCollapsed((v) => !v)} />
      </Box>
    </Box>
  );
}
